import { SubTask } from '@/shared/types/subTask';

export type FlatSubTask = { subTask: SubTask; depth: 0 | 1 };

export type SubTaskRow = {
  task: string | null;
  depth: 0 | 1;
  completed?: boolean | null;
  completedDate?: string | null;
  completedByUserId?: string | null;
  completedByUserName?: string | null;
};

export const isPhase = (subTask: SubTask): boolean => (subTask.subTasks?.length ?? 0) > 0;

export const isCompletable = (subTask: SubTask, depth: 0 | 1): boolean => depth === 1 || !isPhase(subTask);

export const canUpdateTaskSubTasks = (
  existingSubTasks: SubTask[] | null | undefined,
  newSubTasks: SubTask[],
  lockSubTasks: boolean,
): boolean => !lockSubTasks || subTasksStructureEqual(existingSubTasks, newSubTasks);

export const flattenSubTasks = (subTasks?: SubTask[] | null): FlatSubTask[] => {
  const result: FlatSubTask[] = [];

  for (const subTask of subTasks ?? []) {
    result.push({ subTask, depth: 0 });

    for (const child of subTask.subTasks ?? []) {
      result.push({ subTask: child, depth: 1 });
    }
  }

  return result;
};

export const firstIncompleteCompletableSubTask = (subTasks?: SubTask[] | null) =>
  flattenSubTasks(subTasks).find(({ subTask, depth }) => isCompletable(subTask, depth) && !subTask.completed);

export const collectSubTaskCompletedByUserIds = (subTasks?: SubTask[] | null): string[] => {
  const ids = new Set<string>();

  for (const subTask of subTasks ?? []) {
    if (subTask.completedByUserId) {
      ids.add(subTask.completedByUserId);
    }

    for (const child of subTask.subTasks ?? []) {
      if (child.completedByUserId) {
        ids.add(child.completedByUserId);
      }
    }
  }

  return [...ids];
};

const applySubTaskCompletionMetadataToItem = (item: SubTask, userId: string) => {
  if (item.completed) {
    if (!item.completedDate) {
      item.completedDate = new Date().toISOString();
    }

    if (!item.completedByUserId) {
      item.completedByUserId = userId;
    }
  } else {
    delete item.completedDate;
    delete item.completedByUserId;
  }
};

export const applySubTaskCompletionMetadata = (subTasks: SubTask[], userId: string) => {
  for (const subTask of subTasks) {
    applySubTaskCompletionMetadataToItem(subTask, userId);

    for (const child of subTask.subTasks ?? []) {
      applySubTaskCompletionMetadataToItem(child, userId);
    }
  }
};

export const defaultDepthForNewItem = (rows: { depth: 0 | 1 }[]): 0 | 1 => {
  if (rows.length === 0) {
    return 0;
  }

  const last = rows[rows.length - 1];

  if (last.depth === 1) {
    return 1;
  }

  return 0;
};

const subTaskFromRow = (row: SubTaskRow, order: number): SubTask => {
  const item: SubTask = { task: row.task, order, subTasks: null };

  if (row.completed != null) {
    item.completed = row.completed;
  }

  if (row.completed) {
    if (row.completedDate) {
      item.completedDate = row.completedDate;
    }

    if (row.completedByUserId) {
      item.completedByUserId = row.completedByUserId;
    }
  }

  return item;
};

export const rowsToSubTasks = (rows: SubTaskRow[]): SubTask[] => {
  const result: SubTask[] = [];
  let currentPhase: SubTask | null = null;

  for (const row of rows) {
    if (row.depth === 0) {
      const item = subTaskFromRow(row, result.length);
      result.push(item);
      currentPhase = item;
    } else if (currentPhase) {
      const item = subTaskFromRow(row, currentPhase.subTasks?.length ?? 0);

      if (!currentPhase.subTasks) {
        currentPhase.subTasks = [];
      }

      currentPhase.subTasks.push(item);
      delete currentPhase.completed;
      delete currentPhase.completedDate;
      delete currentPhase.completedByUserId;
    } else {
      result.push(subTaskFromRow(row, result.length));
    }
  }

  for (const subTask of result) {
    if (!subTask.subTasks?.length) {
      subTask.subTasks = null;
    } else {
      delete subTask.completed;
      delete subTask.completedDate;
      delete subTask.completedByUserId;
    }
  }

  return result;
};

const normalizeSubTaskForComparison = (subTask: SubTask, order: number): SubTask => {
  const item: SubTask = {
    task: subTask.task,
    order: subTask.order ?? order,
    subTasks: null,
  };

  if (subTask.subTasks?.length) {
    item.subTasks = subTask.subTasks.map((child, index) => ({
      task: child.task,
      order: child.order ?? index,
      subTasks: null,
    }));
  }

  return item;
};

export const normalizeSubTasksForComparison = (subTasks?: SubTask[] | null): SubTask[] =>
  (subTasks ?? []).map((subTask, index) => normalizeSubTaskForComparison(subTask, index));

export const subTasksStructureEqual = (a?: SubTask[] | null, b?: SubTask[] | null): boolean =>
  JSON.stringify(normalizeSubTasksForComparison(a)) === JSON.stringify(normalizeSubTasksForComparison(b));

export const buildSubTasksFromForm = (
  titles: string[],
  depths: (string | number)[],
  completeds?: (boolean | null | undefined)[],
): SubTask[] => {
  const rows: SubTaskRow[] = titles.map((task, index) => ({
    task,
    depth: (parseInt(String(depths[index] ?? '0'), 10) || 0) as 0 | 1,
    completed: completeds?.[index],
  }));

  return rowsToSubTasks(rows);
};

export const buildSubTasksFromFormBody = (
  subTaskTitles?: string | string[] | null,
  subTaskDepths?: string | string[] | null,
  subTaskCompleteds?: (boolean | null | undefined)[] | boolean | null,
): SubTask[] => {
  if (!Array.isArray(subTaskTitles)) {
    return [];
  }

  return buildSubTasksFromForm(
    subTaskTitles,
    Array.isArray(subTaskDepths) ? subTaskDepths : [],
    Array.isArray(subTaskCompleteds) ? subTaskCompleteds : undefined,
  );
};
